import { useCallback, useContext, useRef } from "react";
import { NavigationPendingContext } from "@/components/customs/navigation-pending/NavigationPendingContext";

const MOVE_THRESHOLD_PX = 12;

/**
 * For links over scrollable areas (e.g. reels): navigate only on tap/click, not when
 * the user dragged to scroll (pointer moved beyond a small threshold).
 */
export function useNavigateOnTapOnly() {
  const pendingCtx = useContext(NavigationPendingContext);
  const origin = useRef<{ x: number; y: number } | null>(null);
  const moved = useRef(false);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    moved.current = false;
    origin.current = { x: e.clientX, y: e.clientY };

    const onMove = (ev: PointerEvent) => {
      if (!origin.current) return;
      const dx = ev.clientX - origin.current.x;
      const dy = ev.clientY - origin.current.y;
      if (Math.hypot(dx, dy) > MOVE_THRESHOLD_PX) {
        moved.current = true;
      }
    };

    const cleanup = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", cleanup);
      document.removeEventListener("pointercancel", cleanup);
      origin.current = null;
    };

    document.addEventListener("pointermove", onMove, { passive: true });
    document.addEventListener("pointerup", cleanup);
    document.addEventListener("pointercancel", cleanup);
  }, []);

  const onClick = useCallback((e: React.MouseEvent) => {
    if (moved.current) {
      e.preventDefault();
      e.stopPropagation();
      pendingCtx?.clearNavigationPending();
    }
    moved.current = false;
  }, [pendingCtx]);

  return { onPointerDown, onClick };
}
